import React, { useCallback, useEffect, useState } from 'react';

import { compose } from 'recompose';

import { withAuthorization } from '../../components/Session';
import { Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { Link, RouteComponentProps } from 'react-router-dom';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import * as ROLES from '../../constants/roles';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import { StyledTable, StyledTableHead, StyledTableCell, StyledTableRow } from '../../components/Table';

import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';
import { StyledAsyncSelect as AsyncSelect } from '../../components/form';

import Spinner from '../../components/Spinner';
import { openConfirmDialog } from '../../components/Dialog';
import { FreightForwarder, User, WithAuthorization } from '../../../types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

const styles = (theme: Theme) => ({
  marginRight: {
    marginRight: theme.spacing(1),
  },
  tableWrapper: {
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  moreButton: {
    color: theme.palette.primary.main,
    minWidth: 'unset',
    padding: theme.spacing(1),
  },
  editMenu: {
    color: theme.palette.primary.main,
  },
  deleteMenu: {
    color: theme.palette.error.main,
  },
  outlineButton: {
    borderRadius: '100px',
  },
  spacer: {
    flex: 'unset',
  },
});

type UserTableProps = {
  users: User[];
  onPageChange: (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
} & WithStyles<typeof styles>;

const UsersTable = ({
  users,
  onPageChange,
  onEditUser,
  onDeleteUser,
  currentPage,
  itemsPerPage,
  totalItems,
  classes,
}: UserTableProps) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);

  const openMenu = (row: User) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // https://medium.freecodecamp.org/reactjs-pass-parameters-to-event-handlers-ca1f5c422b9
    setAnchorEl(event.currentTarget);
    setOpenMenuRowId(row._id);
  };

  const closeMenu = () => {
    setOpenMenuRowId(null);
  };

  return (
    <div className={classes.tableWrapper}>
      <StyledTable size="small">
        <StyledTableHead>
          <TableRow>
            <StyledTableCell>Id</StyledTableCell>
            <StyledTableCell width={100}>Name</StyledTableCell>
            <StyledTableCell width={100}>Email</StyledTableCell>
            <StyledTableCell>Home Country</StyledTableCell>
            <StyledTableCell>Roles</StyledTableCell>
            <StyledTableCell>Freight Forwarder</StyledTableCell>
            <StyledTableCell>Quotes</StyledTableCell>
            <StyledTableCell>Ratesheets</StyledTableCell>
            <StyledTableCell>Actions</StyledTableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {users.map((row) => (
            <StyledTableRow key={row._id}>
              <StyledTableCell>{row._id}</StyledTableCell>
              <StyledTableCell>{row.name}</StyledTableCell>
              <StyledTableCell>{row.email}</StyledTableCell>
              <StyledTableCell>{row.home_country_code}</StyledTableCell>
              <StyledTableCell>{row.roles && row.roles.join(', ')}</StyledTableCell>
              <StyledTableCell>{row.freight_forwarder ? row.freight_forwarder.name : ''}</StyledTableCell>
              <StyledTableCell>{row.quoteCount}</StyledTableCell>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell>
                <Button
                  variant="contained"
                  color="secondary"
                  className={classes.moreButton}
                  aria-owns={anchorEl ? `simple-menu-${row._id}` : undefined}
                  aria-haspopup="true"
                  onClick={openMenu(row)}
                >
                  <FontAwesomeIcon icon={faEllipsisH as IconProp} />
                </Button>
                <Menu
                  id={`simple-menu-${row._id}`}
                  anchorEl={anchorEl}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  getContentAnchorEl={null}
                  open={openMenuRowId === row._id}
                  onClose={closeMenu}
                >
                  <MenuItem
                    className={classes.editMenu}
                    onClick={() => {
                      closeMenu();
                      onEditUser(row);
                    }}
                  >
                    Edit User
                  </MenuItem>
                  <MenuItem
                    className={classes.deleteMenu}
                    onClick={() => {
                      closeMenu();
                      onDeleteUser(row);
                    }}
                  >
                    Delete User
                  </MenuItem>
                </Menu>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            {totalItems > 0 && (
              <TablePagination
                rowsPerPageOptions={[itemsPerPage]}
                count={totalItems}
                rowsPerPage={itemsPerPage}
                page={currentPage}
                classes={{ spacer: classes.spacer }}
                backIconButtonProps={{
                  'aria-label': 'Previous Page',
                }}
                nextIconButtonProps={{
                  'aria-label': 'Next Page',
                }}
                onPageChange={onPageChange}
              />
            )}
          </TableRow>
        </TableFooter>
      </StyledTable>
    </div>
  );
};

type UserIndexProps = WithAuthorization & RouteComponentProps<any> & WithStyles<typeof styles> & {};
const UserIndexPage = ({ firebase, history, classes }: UserIndexProps) => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [itemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedFreightForwarder, setSelectedFreightForwarder] = useState<FreightForwarder>();

  const loadUsers = useCallback(
    (where = {}) => {
      setLoading(true);
      firebase
        .users(where, { itemsPerPage, currentPage: currentPage + 1, includeCounts: true })
        .then((response) => {
          let { users, totalItems } = response;
          setLoading(false);
          setUsers(users);
          setTotalItems(totalItems);
        })
        .catch((err) => {
          setLoading(false);
        });
    },
    [firebase, itemsPerPage, currentPage]
  );
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handlePageChange = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
    setCurrentPage(page);
  };

  const editUser = (user: User) => {
    history.push(`users/${user._id}`);
  };
  const deleteUser = (user: User) => {
    openConfirmDialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete User: ${user.name}?`,
    }).then((res: string) => {
      if (res === 'OK') {
        firebase.userDelete(user).then(() => {
          setUsers((users) => users.filter((u) => u._id !== user._id));
        });
      }
    });
  };

  const handleSelectChange = (field: string, selectedFreightForwarder: FreightForwarder) => {
    setSelectedFreightForwarder(selectedFreightForwarder);
    loadUsers({ freight_forwarder_id: selectedFreightForwarder ? selectedFreightForwarder._id : undefined });
  };

  const getFreightForwarders = (where: any, textQuery: { itemsPerPage: number; currentPage: number }) => {
    return firebase.freightForwarders(where, textQuery).then((data) => {
      let { freightForwarders } = data;
      return freightForwarders;
    });
  };

  return (
    <Grid container spacing={3}>
      <Spinner visible={loading} />
      <Grid item xs={12}>
        <Typography variant="h1">Users</Typography>
      </Grid>
      <Grid item xs={3}>
        <Button
          component={Link}
          to={`/admin/users/new`}
          size="large"
          variant="outlined"
          color="secondary"
          className={classes.outlineButton}
        >
          <FontAwesomeIcon icon={faPlus as IconProp} />
          Add User
        </Button>
      </Grid>
      <Grid item xs={3}>
        <AsyncSelect
          value={selectedFreightForwarder}
          getOptionValue={(option: any) => option._id}
          getOptionLabel={(option: any) => option.name}
          onChange={(value: FreightForwarder) => handleSelectChange('selectedServiceProvider', value)}
          loadOptions={(textQuery: any) => getFreightForwarders({}, textQuery)}
          placeholder="Freight Forwarder"
          dark={true}
        />
      </Grid>
      <Grid item xs={12}>
        <UsersTable
          users={users}
          classes={classes}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onEditUser={editUser}
          onDeleteUser={deleteUser}
        />
      </Grid>
    </Grid>
  );
};

const condition = (authUser: User) => authUser && authUser.roles && authUser.roles.includes(ROLES.ADMIN);

export default compose<UserIndexProps, {}>(
  withAuthorization(condition),
  withStyles(styles, { withTheme: true })
)(UserIndexPage);
